import { isDefined, isNotBlank } from '../../reducers/util';
import { LabelAndKey } from '../../components/create/types/LabelAndKey.js';
import { LabelAndValue } from '../../components/create/types/LabelAndValue.js';
import {Criterion, CriterionHes, CriterionPcad, Group, Period, Query} from "../HealthcareUsageReport/types/CohortQuery";
import _ from 'lodash';

export const arrayToString = (prefix: string, decorator: (value: string) => string, arr: string[] | undefined) => {
    return (isDefined(arr) && arr.length > 0) ? prefix + decorator(arr.join(', ')) : ''
}

export const arrayToStringComplex = (prefix: string, decorator: (value: string) => string, arr: (LabelAndKey | LabelAndValue)[] | undefined) => {
    return (isDefined(arr) && arr.length > 0) ? prefix + decorator(arr.map(i => i.label).join(', ')) : ''
}

export const ageDescription = (fromAge: (number | undefined), toAge: (number | undefined)) => {
    if (isDefined(fromAge) && isDefined(toAge)) {
        return fromAge + " to " + toAge
    } else if (isDefined(fromAge)) {
        return fromAge + " or higher"
    } else if (isDefined(toAge)) {
        return "less than or equal to " + toAge
    } else {
        return ""
    }
}

export const htmlForType = (text: string) => {
    return '<span class="type">' + text + '</span>: '
}

export const stringForType = (text: string) => {
    return `${text}: `
}

export const htmlForDescription = (text: string) => {
    return '<span class="description">' + text + '</span>'
}

export const stringForDescription = (text: string) => {
    return `${text}`
}

export const htmlForFilter = (text: string) => {
    return '<span class="filter">' + text + '</span>'
}

export const stringForFilter = (text: string) => {
    return `${text}`
}

export const operatorDescription = (operator: string[]) => {
    return operator
        .map(expr => {
            switch (expr) {
                case 'and':
                    return 'And'
                case 'and-before':
                    return 'And Before index'
                case 'and-index':
                    return 'And On Index'
                case 'and-after':
                    return 'And After Index'
                case 'or':
                    return 'Or'
                case 'not':
                    return 'Not'
                case 'not-before':
                    return 'Not Before Index'
                case 'not-index':
                    return 'Not On Index'
                case 'not-after':
                    return 'Not After Index'
                default:
                    return "?"
            }
        })
        .join(', ')
}

const criterionDescriptionHes = (criterionHes: CriterionHes, html = true) => {
    const decorateType = html ? htmlForType : stringForType
    const decorateDescription = html ? htmlForDescription : stringForDescription
    const decorateFilter = html ? htmlForFilter : stringForFilter

    let description = ''
    const diagnosisType = (isDefined(criterionHes.diagnosisType) && criterionHes.diagnosisType.length > 0) ? '(' + criterionHes.diagnosisType.join(' or ') + ')' : ''
    const isOutpatient = criterionHes.dataset === 'outpatient'

    const ageText = ageDescription(criterionHes.fromAge, criterionHes.toAge)
    const admissionMethodText = criterionHes.admissionMethod ? _.startCase(criterionHes.admissionMethod.toString()) : ''
    const genderCamelCased = criterionHes.gender ? criterionHes.gender.map(i => _.startCase(i)) : criterionHes.gender
    const startText = [
        decorateType('Dataset') + decorateDescription(_.startCase(criterionHes.dataset)),
        arrayToString(decorateType('Gender'), decorateDescription, genderCamelCased),
        !isOutpatient ? decorateType('Admission Method') + decorateDescription(admissionMethodText) : '',
        isNotBlank(ageText) ? decorateType('Age') + decorateDescription(ageText) : ''
    ].filter(i => i.length > 0).join(', ')

    description += [
        startText,
        !isOutpatient ? arrayToStringComplex(decorateType('Diagnosis ' + diagnosisType), decorateDescription, criterionHes.diagnosis) : '',
        arrayToStringComplex(decorateType('Procedures'), decorateDescription, criterionHes.procedures),
        arrayToStringComplex(decorateType('Treatments'), decorateDescription, criterionHes.treatments),
        arrayToStringComplex(decorateType('HRGs'), decorateDescription, criterionHes.hrgs),
        arrayToStringComplex(decorateType('Provider Trusts'), decorateDescription, criterionHes.trusts),
        arrayToStringComplex(decorateType('Sub ICB Locations'), decorateDescription, criterionHes.subICBLocations),
        arrayToStringComplex(decorateType('Cohort'), decorateDescription, criterionHes.trackedCohort),
    ].filter(i => i.length > 0).map(i => decorateFilter(i)).join(html ? '' : ', ')
    return description
}


const criterionDescriptionPcad = (criterionPcad: CriterionPcad, html = true) => {
    const decorateType = html ? htmlForType : stringForType
    const decorateDescription = html ? htmlForDescription : stringForDescription
    const decorateFilter = html ? htmlForFilter : stringForFilter

    let description = ''

    const startText = [
        decorateType('Dataset') + decorateDescription(_.startCase(criterionPcad.dataset)),
    ].filter(i => i.length > 0).join(', ')

    description += [
        startText,
        arrayToStringComplex(decorateType('Clinical Event'), decorateDescription, criterionPcad.clinicalEvent),
        arrayToStringComplex(decorateType('Clinical Indication'), decorateDescription, criterionPcad.clinicalIndication),
        arrayToStringComplex(decorateType('Clinical Custom Code List'), decorateDescription, criterionPcad.clinicalEventCustom),
        arrayToStringComplex(decorateType('Treatment VMP'), decorateDescription, criterionPcad.treatmentVMP),
        arrayToStringComplex(decorateType('Treatment Custom Code List'), decorateDescription, criterionPcad.treatmentCustom),
    ].filter(i => i.length > 0).map(i => decorateFilter(i)).join(html ? '' : ', ')
    return description
}

/**
 * TODO: Rewrite the return to be JSON or jsx
 * Creating html as a string and then rendering it has security implications.
 * @returns
 */
export const criterionDescription = (query: Query, criterion: Criterion, html = true) => {
    if (query.dataset === "HES") {
        return criterionDescriptionHes(criterion, html)
    }
    if (query.dataset === "OPCRD") {
        return criterionDescriptionPcad((criterion as CriterionPcad), html);
    }
    return ""

}

export const groupDescription = (query: Query, group: Group, html = true) => {
    let description = html ? '' : group.title + ': '
    description += group
        .criteria
        .flatMap(i => [criterionDescription(query, i, html), operatorDescription(i.operator)])
        .slice(0, -1)
        .join(" ")
    return description
}

export const periodDescription = (period: Period) => {
    let description = [`Observational Period: ${period.from} - ${period.to}`]
    if (isDefined(period.lookback)) {
        description.push(`Look back: ${period.lookback} months`)
    }
    if (isDefined(period.followup)) {
        description.push(`Follow up: ${period.followup} months`)
    }
    return description.join(', ')
}

export const queryDescription = (query: Query) => {
    let description = periodDescription(query.period)
    description += ", " + query
        .groups
        .flatMap(i => [groupDescription(query, i, false), operatorDescription(i.operator)])
        .slice(0, -1)
        .join(" ")

    return description
}